type Direction = "in" | "out";

interface Aim {
  hold(): { seek(progress: number): void; cancel(): void } | null;
  go(): void;
}

export type Find<T> = (direction: Direction, target: T) => Aim | "ocupado" | null;

const rules = {
  complete: 0.28,
  wheel: 200,
  quiet: 150,
  slack: 0.04,
  spread: 0.5,
  squeeze: 0.35,
} as const;

interface Gesture {
  readonly direction: Direction;
  readonly aim: Aim | "ocupado" | null;
  readonly held: ReturnType<Aim["hold"]>;
  progress: number;
}

export function createPinch<T>(find: Find<T>, reduced: () => boolean, later: (run: () => void, ms: number) => () => void) {
  let gesture: Gesture | null = null;
  let travel = 0;
  let unquiet = () => {};

  function begin(direction: Direction, target: T): Gesture {
    const aim = find(direction, target);
    const held = aim === null || aim === "ocupado" || reduced() ? null : aim.hold();
    travel = 0;
    return (gesture = { direction, aim, held, progress: 0 });
  }

  function move(to: number): boolean {
    if (!gesture) return false;
    gesture.progress = Math.min(1, Math.max(0, to));
    gesture.held?.seek(gesture.progress);
    return gesture.aim !== null;
  }

  function end(): void {
    const ended = gesture;
    gesture = null;
    unquiet();
    if (!ended || ended.aim === null || ended.aim === "ocupado") return;
    if (ended.progress > rules.complete) ended.aim.go();
    else ended.held?.cancel();
  }

  return {
    wheel(deltaY: number, target: T): boolean {
      if (!gesture && deltaY === 0) return false;
      const { direction } = gesture ?? begin(deltaY < 0 ? "in" : "out", target);
      travel = Math.min(rules.wheel, Math.max(0, travel + (direction === "in" ? -deltaY : deltaY)));
      unquiet();
      unquiet = later(end, rules.quiet);
      return move(travel / rules.wheel);
    },
    touch(ratio: number, target: T): boolean {
      if (!gesture && Math.abs(ratio - 1) <= rules.slack) return true;
      const { direction } = gesture ?? begin(ratio > 1 ? "in" : "out", target);
      return move(direction === "in" ? (ratio - 1) / rules.spread : (1 - ratio) / rules.squeeze);
    },
    lift: end,
  };
}
